import type { FC } from 'react';
import { useCallback } from 'react';

import { SWReactIcons } from '@similarweb/icons';

import { useAppState } from '../../AppStateProvider';
import { Page } from '../../domain/page';
import { AppMode, appStore } from '../../services/appStore';
import { translate } from '../../services/i18n';
import { useDispatchTrackEventWithLocation } from '../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { CloseExtensionButton } from '../CloseExtensionButton';
import { Tooltip } from '../Tooltip';

import { QuotaView } from './QuotaView';
import { SalesLogo } from './SalesLogo';
import { StyledHeader, StyledLogo, StyledLogoText, StyledSettingsButton } from './styles';

export const Header: FC = () => {
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const { page, settingsStorage, mode } = useAppState();
  const handleSettingsClick = () => {
    appStore.setSettingsPage();
    dispatchTrackEventWithLocation('extension top actions', 'open settings', 'extension settings');
  };

  const isFirstOpen = settingsStorage.isFirstOpen ?? false;
  const isCustomUiVersion = settingsStorage.isCustomUiVersion ?? false;

  const getTooltipProps = useCallback(() => {
    if (isFirstOpen) {
      return {
        content: translate('header.settings.first_open'),
        enabled: true,
        visible: true,
        withOutsideClick: true,
        minWidth: 230,
        onOutsideClick: () => {
          appStore.setSettingsStorageValue({ isFirstOpen: false });
        },
      };
    }

    if (isCustomUiVersion) {
      return {
        content: translate('header.settings.custom_ui_version'),
        enabled: true,
        minWidth: 140,
      };
    }

    return {
      content: '',
      enabled: false,
      minWidth: 0,
    };
  }, [settingsStorage]);

  return (
    <StyledHeader data-automation="Header">
      <StyledLogo>
        <SalesLogo data-automation="HeaderLogo" />
        <StyledLogoText>
          <span>Sales Intelligence</span>
          <span>by Similarweb</span>
        </StyledLogoText>
      </StyledLogo>
      <QuotaView />
      {mode === AppMode.Extension ? (
        <Tooltip placement="bottom-left" {...getTooltipProps()}>
          <StyledSettingsButton
            data-automation="SettingsButton"
            disabled={page === Page.Settings}
            onClick={page === Page.Settings ? undefined : handleSettingsClick}
            variant="flat"
            indicator={settingsStorage.isCustomUiVersion}
          >
            <SWReactIcons iconName="settings" size="xs" />
          </StyledSettingsButton>
        </Tooltip>
      ) : null}

      {mode !== AppMode.Salesforce && mode !== AppMode.Standalone ? <CloseExtensionButton /> : null}
    </StyledHeader>
  );
};
